<template>
  <div class="h3">Варианты размещения</div>
  <div class="paragraph-s_h text-secondary mt-4">Отметьте варианты размещения, которые хотите использовать.</div>
  <div class="paragraph-s_h text-secondary mt-1">
    Все выбранные варианты размещения будут без возможности размещения туриста без места. И цена проживания определяется
    ценой за номер.
  </div>
  <e-form ref="formRef" data-wide-content @submit="submit">
    <div class="d-flex flex-sm-nowrap flex-wrap mt-10 mb-10 col-gap-4 row-gap-4">
      <e-select
        v-model="filter.roomCategory"
        :error="filter.roomCategory && isEmptyPlacingVariants"
        label="Категория номера"
        item-value="cid"
        item-title="name[0].value"
        clearable
        :items="roomCategoryFilterItems"
      />
      <e-select
        v-model="filter.touristType"
        label="Тип туриста"
        clearable
        :error="filter.touristType && isEmptyPlacingVariants"
        :items="touristTypeFilterItems"
      />
      <e-checkbox v-model="filter.selectedOnly" label="Tолько выбранные" />
    </div>

    <e-alert v-if="(filter.roomCategory || filter.touristType) && isEmptyPlacingVariants" variant="error" thin>
      Для некоторых категорий номеров не выбраны варианты размещения
    </e-alert>

    <e-expansion-panels v-if="isFetchLoading" :model-value="[0]" data-wide-content>
      <e-expansion-panel>
        <template #title>
          <skeleton-loader width="160px" height="24px" />
        </template>

        <e-data-items :model-value="[]" :headers="headers" :items="[]" loading />
      </e-expansion-panel>
    </e-expansion-panels>

    <template v-else>
      <e-expansion-panels v-for="(variant, index) in filteredPlacingVariants" :key="index" default-opened class="mb-10">
        <e-expansion-panel>
          <template #title>{{ variant.roomCategory.name[0].value }}</template>
          <template #titleSecondary>
            <e-alert v-if="formRef.isDirty && variant.selected.length === 0" variant="error" thin>
              Для категории номера должен быть выбран хотя бы один вариант размещения
            </e-alert>
          </template>
          <e-data-items
            v-model="getPlacingVariantById(variant.roomCategory.cid)!.selected"
            return-object
            :items="variant.suggestedPlacingVariants"
            :headers="headers"
            :show-only-selected="filter.selectedOnly"
          >
            <template v-slot:[`header.data-table-select`]="props">
              <e-checkbox
                :model-value="props.allSelected"
                :square-selected="props.someSelected"
                @update:modelValue="onSelectAllClick(getPlacingVariantById(variant.roomCategory.cid)!, $event)"
              />
            </template>
            <template v-slot:[`item.data-table-select`]="{ isSelected, internalItem, toggleSelect }">
              <e-checkbox
                :model-value="isSelected(internalItem)"
                :disabled="variant.selected.length <= 1 && isSelected(internalItem)"
                @update:modelValue="toggleSelect(internalItem, $event)"
              />
            </template>
            <template v-slot:[`item.dataItemSecondRow`]="{ isSelected, internalItem }">
              <div
                v-if="variant.selected.length === 1 && isSelected(internalItem)"
                class="paragraph-xs text-secondary d-flex align-center font-400"
              >
                <e-icon name="info" :size="16" class="text-blue mr-2" />
                Для категории номера должен быть хотя бы один вариант размещения
              </div>
            </template>
            <template v-slot:[`item.name`]="{ value, item }">
              {{ value }}
              <help-tooltip>
                <placing-variants-tooltip :placing-variant="item.placingVariant" />
              </help-tooltip>
            </template>
            <template v-slot:[`item.withoutPlace`]> 0 </template>
            <template v-slot:[`item.price`]> за номер </template>
          </e-data-items>
        </e-expansion-panel>
      </e-expansion-panels>
    </template>
  </e-form>

  <div class="d-flex justify-space-between mt-10">
    <e-btn variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
    <e-btn append-icon-src="arrowRight" :loading="isLoading" variant="primary" @click="formRef?.submit"> Далее </e-btn>
  </div>

  <confirmation-dialog v-model="isConfirmationDialogOpen" confirm-text="Завершить" :action="update">
    <div class="h3 mb-6">Завершение работы мастера</div>
    <div class="text-secondary font-600 mb-2">Убедитесь, что внесли правильные данные!</div>
    <div class="text-secondary">
      Дальнейшее редактирование настроек подключения возможно только через перезапуск мастера
    </div>
  </confirmation-dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ArrayElementType } from '@/core/types/gengeric';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import ConfirmationDialog from '@/core/components/modals/ConfirmationDialog.vue';
import {
  EAlert,
  EBtn,
  ECheckbox,
  EExpansionPanel,
  EExpansionPanels,
  EForm,
  EIcon,
  ESelect,
  HelpTooltip,
} from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { distinctArray } from '@/core/utils/array';
import { deepCloneObject } from '@/core/utils/object';
import PlacingVariantsTooltip from '@/parts/tariffs/components/PlacingVariantsTooltip.vue';
import { type PlacingVariantsTarification, useReverseIntegrationStore } from '@/parts/tariffs/store/reverse';
import { touristTypeString } from '@/parts/tariffs/utils/tariffs';

const { back, getPlacingVariantsTarification, updatePlacingVariants, setActiveStep } = useReverseIntegrationStore();

const filter = ref({
  roomCategory: null,
  touristType: null,
  selectedOnly: false,
});

const formRef = ref();

type PlacingVariant = PlacingVariantsTarification & {
  selected: ArrayElementType<PlacingVariantsTarification['suggestedPlacingVariants']>[];
};

const placingVariants = ref<PlacingVariant[]>([]);

const roomCategoryFilterItems = computed(() => {
  return placingVariants.value.map((variant) => variant.roomCategory);
});

const touristTypeFilterItems = computed(() =>
  distinctArray(
    placingVariants.value.reduce<string[]>((res, variant) => {
      res = [
        ...res,
        ...variant.suggestedPlacingVariants.reduce<string[]>((res2, variant) => {
          res2 = [
            ...res2,
            ...variant.placingVariant.baseSeats.map((seat) => touristTypeString(seat)),
            ...variant.placingVariant.extSeats.map((seat) => touristTypeString(seat)),
          ];

          return res2;
        }, []),
      ];

      return res;
    }, []),
  ).filter((t) => t !== 'Весь номер'),
);

const filteredPlacingVariants = computed(() => {
  let filtered = deepCloneObject(placingVariants.value);

  if (filter.value.roomCategory) {
    filtered = filtered.filter((variant) => variant.roomCategory.cid === filter.value.roomCategory);
  }

  if (filter.value.touristType) {
    filtered.forEach((variant) => {
      variant.suggestedPlacingVariants = variant.suggestedPlacingVariants.filter(
        (variant) =>
          variant.placingVariant.baseSeats.some((seat) => touristTypeString(seat) === filter.value.touristType) ||
          variant.placingVariant.extSeats.some((seat) => touristTypeString(seat) === filter.value.touristType),
      );
    });
  }

  return filtered;
});

const onSelectAllClick = (variant: PlacingVariant, value: boolean) => {
  if (value) {
    variant.selected = variant.suggestedPlacingVariants;
  } else {
    variant.selected = [variant.selected[0]];
  }
};

const isLoading = ref(false);
const isConfirmationDialogOpen = ref(false);

const isEmptyPlacingVariants = computed(
  () => formRef.value?.isDirty && placingVariants.value.some((variant) => variant.selected.length === 0),
);

const submit = async () => {
  if (isEmptyPlacingVariants.value) {
    return;
  }

  isConfirmationDialogOpen.value = true;
};

const update = async () => {
  isLoading.value = true;

  const response = await updatePlacingVariants(
    placingVariants.value.map((variant) => ({
      roomCategoryCid: variant.roomCategory.cid,
      variants: variant.selected.map((variant) => ({
        defaultAvailable: true,
        isNeedConfirm: false,
        maxNoSeatQuantity: 0,
        pricePerRoom: true,
        shortName: variant.name,
        placings: {
          baseSeats: variant.placingVariant.baseSeats.map((s) => ({
            seats: s.seats,
            touristType: {
              ageCategory: s.touristType.ageCategory
                ? {
                    from: s.touristType.ageCategory.from,
                    to: s.touristType.ageCategory.to,
                  }
                : null,
            },
          })),
          extSeats: variant.placingVariant.extSeats.map((s) => ({
            seats: s.seats,
            touristType: {
              ageCategory: s.touristType.ageCategory
                ? {
                    from: s.touristType.ageCategory.from,
                    to: s.touristType.ageCategory.to,
                  }
                : null,
            },
          })),
        },
      })),
    })),
  );

  isLoading.value = false;

  if (response.data) {
    setActiveStep(6);
  }
};

const loadData = async () => {
  const response = await getPlacingVariantsTarification();

  if (response.data?.reversePlacingVariants?.placingVariantsTarification) {
    placingVariants.value =
      response.data.reversePlacingVariants.placingVariantsTarification.map((variant) => ({
        ...variant,
        selected: variant.suggestedPlacingVariants.filter((variant) => variant.isChecked),
      })) || [];
  }
};

const getPlacingVariantById = (id: string) => {
  return placingVariants.value.find((variant) => variant.roomCategory.cid === id);
};

const { isLoading: isFetchLoading } = useFetch(loadData);

const headers = [
  {
    key: 'name',
    title: 'Вариант размещения',
    sortable: false,
  },
  {
    key: 'withoutPlace',
    title: 'Туристы без места',
    sortable: false,
  },
  {
    key: 'price',
    title: 'Цена',
    sortable: false,
  },
];
</script>

<style scoped lang="scss"></style>
